import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { writeFeatureFlagOverrides } from "@/features/config/featureFlagOverrides";
import {
  FEATURE_FLAG_KEYS,
  useFeatureFlags,
  type FeatureFlags,
} from "@/features/config/useFeatureFlags";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useEffect, useMemo, useRef, useState } from "react";

const formatFlagValue = (value: boolean) => (value ? "true" : "false");

const isDraftOverridden = (
  key: keyof FeatureFlags,
  draft: FeatureFlags,
  remoteFlags: FeatureFlags,
) => draft[key] !== remoteFlags[key];

export const FeatureFlagsContent = () => {
  const { flags, remoteFlags, isLoading } = useFeatureFlags();
  const [draft, setDraft] = useState<FeatureFlags | null>(null);
  const draftInitializedRef = useRef(false);

  useEffect(() => {
    if (isLoading || draftInitializedRef.current) return;
    setDraft(flags);
    draftInitializedRef.current = true;
  }, [flags, isLoading]);

  const isDirty = useMemo(() => {
    if (!draft) return false;
    return FEATURE_FLAG_KEYS.some((key) => draft[key] !== flags[key]);
  }, [draft, flags]);

  const handleSave = () => {
    if (!draft) return;

    const nextOverrides: Partial<FeatureFlags> = {};
    for (const key of FEATURE_FLAG_KEYS) {
      if (draft[key] !== remoteFlags[key]) {
        nextOverrides[key] = draft[key];
      }
    }

    writeFeatureFlagOverrides(nextOverrides);
    window.location.reload();
  };

  const handleClearOverride = (key: keyof FeatureFlags) => {
    setDraft((prev) => (prev ? { ...prev, [key]: remoteFlags[key] } : prev));
  };

  if (isLoading || !draft) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-zinc-400 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-[3px] border border-zinc-200">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-zinc-500">
            <tr>
              <th className="px-4 py-3 font-medium">Flag</th>
              <th className="px-4 py-3 font-medium">Remote</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Value</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {FEATURE_FLAG_KEYS.map((key) => {
              const draftOverridden = isDraftOverridden(key, draft, remoteFlags);
              return (
                <tr key={key}>
                  <td className="px-4 py-3 font-mono text-zinc-900">{key}</td>
                  <td className="px-4 py-3">
                    <Badge variant={remoteFlags[key] ? "green" : "default"}>
                      {formatFlagValue(remoteFlags[key])}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={draftOverridden ? "blue" : "default"}>
                      {draftOverridden ? "Overridden" : "Default"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      className="h-8 rounded-[3px] border border-zinc-200 bg-white px-2 text-base min-w-[6rem]"
                      value={formatFlagValue(draft[key])}
                      onChange={(event) => {
                        const value = event.target.value === "true";
                        setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
                      }}
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={!draftOverridden}
                      onClick={() => handleClearOverride(key)}
                    >
                      Clear override
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-end gap-3">
        <Button type="button" disabled={!isDirty} onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
};

export const FeatureFlagsPage = () => {
  useDocumentTitle("Feature flags");
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl px-8 py-8">
        <header className="mb-10">
          <h1 className="font-display text-4xl leading-none tracking-tight text-zinc-900">
            Feature flags
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-600">
            Local development only. Overrides are stored in localStorage.
          </p>
        </header>
        <FeatureFlagsContent />
      </div>
    </div>
  );
};
